import { ReactNode } from "react";
import { FESymbols, MatchedLine, multiply as Multiply, Symbols } from "./type";
import BigNumber from "bignumber.js";
import Utils from "../../utils";

const HunkCaiShenUtils = {
  takeWildRealId(x: number, y: number, multiply: Multiply[]): number {
    const point = multiply.find((m) => m.x === x && m.y === y);
    if (point) {
      return +("820" + (point.value - 2));
    }
    return 0;
  },
  convertLine(
    reelMatrix: number[][],
    lineId: number,
    line: MatchedLine
  ): {
    lineDraw: ReactNode;
    symbolId: number;
  } {
    const maxY = reelMatrix[0].length;
    const symbolId =
      line.points
        .map((point) => reelMatrix[point.x][point.y])
        .find((symbol) => symbol !== FESymbols.Wild) || 0;
    return {
      lineDraw: Utils.drawLine(maxY, this.takePayline(lineId)),
      symbolId,
    };
  },
  convertSymbol(symbol: Symbols, key?: string): ReactNode {
    if (Number.isNaN(symbol)) return "";
    switch (symbol) {
      case Symbols.Wild3:
        return <img key={key} alt="8201" src="/hcs/8201.png" />;
      case Symbols.Wild4:
        return <img key={key} alt="8202" src="/hcs/8202.png" />;
      case Symbols.Wild5:
        return <img key={key} alt="8203" src="/hcs/8203.png" />;
      case Symbols.Scatter:
        return <img key={key} alt="25" src="/hcs/25.png" />;
      case Symbols.Symbol1:
        return <img key={key} alt="201" src="/hcs/201.png" />;
      case Symbols.Symbol2:
        return <img key={key} alt="202" src="/hcs/202.png" />;
      case Symbols.Symbol3:
        return <img key={key} alt="203" src="/hcs/203.png" />;
      case Symbols.Symbol4:
        return <img key={key} alt="204" src="/hcs/204.png" />;
      case Symbols.Symbol5:
        return <img key={key} alt="205" src="/hcs/205.png" />;
      case Symbols.Symbol6:
        return <img key={key} alt="206" src="/hcs/206.png" />;
      case Symbols.LowSymbol1:
        return <img key={key} alt="101" src="/hcs/101.png" />;
      case Symbols.LowSymbol2:
        return <img key={key} alt="102" src="/hcs/102.png" />;
      case Symbols.LowSymbol3:
        return <img key={key} alt="103" src="/hcs/103.png" />;
      case Symbols.LowSymbol4:
        return <img key={key} alt="104" src="/hcs/104.png" />;
      default:
        return symbol;
    }
  },
  takePayline(lineId: number): number[] {
    const LINE_PATTERNS = [
      [], // start from 1
      [1, 1, 1, 1, 1],
      [0, 0, 0, 0, 0],
      [2, 2, 2, 2, 2],
      [0, 1, 2, 1, 0],
      [2, 1, 0, 1, 2],
    ];

    return LINE_PATTERNS[lineId];
  },
  symbolPayouts(symbol: Symbols): { [k: number]: number } {
    const payouts = {
      [Symbols.Wild3]: { 1: 3 },
      [Symbols.Wild4]: { 1: 4 },
      [Symbols.Wild5]: { 1: 5 },
      [Symbols.Scatter]: { 1: 1, 2: 3 },
      [Symbols.Symbol1]: { 3: 100, 4: 250, 5: 1000 },
      [Symbols.Symbol2]: { 3: 50, 4: 150, 5: 600 },
      [Symbols.Symbol3]: { 3: 25, 4: 75, 5: 300 },
      [Symbols.Symbol4]: { 3: 10, 4: 30, 5: 100 },
      [Symbols.Symbol5]: { 3: 8, 4: 25, 5: 75 },
      [Symbols.Symbol6]: { 3: 6, 4: 20, 5: 60 },
      [Symbols.LowSymbol1]: { 3: 3, 4: 12, 5: 40 },
      [Symbols.LowSymbol2]: { 3: 3, 4: 12, 5: 40 },
      [Symbols.LowSymbol3]: { 3: 2, 4: 8, 5: 30 },
      [Symbols.LowSymbol4]: { 3: 2, 4: 8, 5: 30 },
    };
    return payouts[symbol];
  },
  explainationSymbolPayout(
    s: Symbols,
    labelAtRight: boolean | number = false
  ): ReactNode {
    const wrapDivClass = [];
    if (typeof labelAtRight === "boolean" && labelAtRight) {
      wrapDivClass.push("d-flex");
      wrapDivClass.push("mb-2");
      wrapDivClass.push("align-items-center");
      wrapDivClass.push("border-bottom");
      wrapDivClass.push("border-dark");
    } else {
      wrapDivClass.push("text-center");
    }
    return (
      <div key={`explanation-${s}`} className={wrapDivClass.join(" ")}>
        <div className="d-inline-block symbol">
          {HunkCaiShenUtils.convertSymbol(s)}
        </div>
        <div>
          {Object.entries(HunkCaiShenUtils.symbolPayouts(s)).map(
            ([count, payout], i) => {
              let payoutStr = "x" + payout;
              if (s === Symbols.Scatter) {
                payoutStr += " of Bet";
              }
              if (
                s === Symbols.Wild3 ||
                s === Symbols.Wild4 ||
                s === Symbols.Wild5
              ) {
                payoutStr += " of Line Win";
              }
              return (
                <div key={`symbol-${s}-${i}`}>
                  {count} : {payoutStr}
                </div>
              );
            }
          )}
        </div>
      </div>
    );
  },
  maxBetFormular(amount: number | BigNumber): BigNumber {
    return BigNumber(amount).multipliedBy(50); // buy free spin rate
  },
  maxPayoutFormular(amount: number | BigNumber): BigNumber {
    return BigNumber(amount)
      .multipliedBy(0.2) // bet size
      .multipliedBy(5) // 5 lines
      .multipliedBy(5 * 3) // wild bonus multiplier 3 wild
      .multipliedBy(1000); // symbol payout
  },
} as const;

export default HunkCaiShenUtils;
